import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { openApi } from "../../services/api";
import "./publicTicketPortalPage.css";

const BRAND = "TicketDesk";

const storageKey = (companyId, ticketId) =>
  `publicPortal_${companyId}_ticket_${ticketId}_token`;

const initials = (name) => {
  const p = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (p.length === 0) return "CL";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return `${p[0][0]}${p[p.length - 1][0]}`.toUpperCase();
};

const formatMsgTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dm = `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}`;
    const hm = `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}`;
    return `${dm} ${hm}`;
  } catch {
    return "";
  }
};

const PublicTicketPortalPage = () => {
  const { companyId } = useParams();
  const base = `/public-portal/${companyId || ""}`;

  const [view, setView] = useState("home");
  const [panelConfig, setPanelConfig] = useState(null);
  const [toast, setToast] = useState({ show: false, text: "" });
  const [loading, setLoading] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState({
    open: false,
    loading: false,
    messageId: null,
    url: "",
    filename: "",
    kind: "file", // "pdf" | "image" | "video" | "file"
  });
  const attachmentPreviewUrlRef = useRef("");

  const [protocolInput, setProtocolInput] = useState("");
  const [accessPhoneInput, setAccessPhoneInput] = useState("");

  const [activeTicketId, setActiveTicketId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [detail, setDetail] = useState(null);
  const [linkedPortalTickets, setLinkedPortalTickets] = useState([]);

  const [portalPhone, setPortalPhone] = useState("");
  const [portalCategoryStageId, setPortalCategoryStageId] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState({});

  const activeCustomFields = useMemo(() => {
    const list = panelConfig?.customFields;
    if (!Array.isArray(list)) return [];
    return list.filter((f) => String(f?.question || "").trim());
  }, [panelConfig]);

  const option2Stages = useMemo(() => {
    const list = panelConfig?.stages?.["2"];
    if (!Array.isArray(list)) return [];
    return list
      .map((s) => ({
        id: String(s?.id ?? "").trim(),
        name: String(s?.name ?? "").trim() || "—",
      }))
      .filter((s) => s.id);
  }, [panelConfig]);

  const option2SelectLabel = useMemo(() => {
    const t = String(panelConfig?.optionNames?.["2"] ?? "").trim();
    return t || "Opção 2";
  }, [panelConfig]);

  useEffect(() => {
    setCustomFieldValues((prev) => {
      const next = { ...prev };
      activeCustomFields.forEach((f) => {
        if (!(f.id in next)) next[f.id] = "";
      });
      Object.keys(next).forEach((k) => {
        if (!activeCustomFields.some((f) => f.id === k)) delete next[k];
      });
      return next;
    });
  }, [activeCustomFields]);

  const [msgDraft, setMsgDraft] = useState("");
  const [pendingChatFiles, setPendingChatFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const prevChatDigestRef = useRef("");
  const fileChatRef = useRef(null);

  const showToast = useCallback((text) => {
    setToast({ show: true, text });
    setTimeout(() => setToast({ show: false, text: "" }), 3500);
  }, []);

  const guessAttachmentKind = (contentType, filename) => {
    const ct = String(contentType || "").toLowerCase();
    const name = String(filename || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";

    if (ct.includes("pdf") || ext === "pdf") return "pdf";
    if (
      ct.startsWith("image/") ||
      ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)
    ) {
      return "image";
    }
    if (ct.startsWith("video/") || ["mp4", "webm", "ogg", "mov", "m4v"].includes(ext)) {
      return "video";
    }
    return "file";
  };

  const closeAttachmentPreview = useCallback(() => {
    setAttachmentPreview((s) => ({ ...s, open: false, loading: false }));
    if (attachmentPreviewUrlRef.current) {
      try {
        URL.revokeObjectURL(attachmentPreviewUrlRef.current);
      } catch {
        /* ignore */
      }
      attachmentPreviewUrlRef.current = "";
    }
    setTimeout(() => {
      setAttachmentPreview((s) => ({
        ...s,
        messageId: null,
        url: "",
        filename: "",
        kind: "file",
      }));
    }, 0);
  }, []);

  useEffect(() => {
    if (!attachmentPreview.open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAttachmentPreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [attachmentPreview.open, closeAttachmentPreview]);

  const loadPanelConfig = useCallback(async () => {
    if (!companyId) {
      setPanelConfig(null);
      return;
    }
    try {
      const { data } = await openApi.get(`${base}/panel-config`);
      setPanelConfig(data);
    } catch {
      setPanelConfig(null);
    }
  }, [base, companyId]);

  useEffect(() => {
    loadPanelConfig();
  }, [loadPanelConfig]);

  const scrollMsgs = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  useEffect(() => {
    prevChatDigestRef.current = "";
  }, [activeTicketId]);

  useEffect(() => {
    const msgs = detail?.messages;
    if (!msgs || !Array.isArray(msgs)) return;
    const last = msgs.length ? msgs[msgs.length - 1] : null;
    const digest = `${msgs.length}:${last?.id ?? ""}:${last?.createdAt ?? ""}`;
    if (digest === prevChatDigestRef.current) return;
    prevChatDigestRef.current = digest;
    scrollMsgs();
  }, [detail?.messages]);

  useEffect(() => {
    if (view !== "chat" || activeTicketId == null || !accessToken) {
      return undefined;
    }
    const poll = async () => {
      try {
        const { data } = await openApi.get(`${base}/tickets/${activeTicketId}`, {
          headers: { "X-Ticket-Access-Token": accessToken },
        });
        setDetail(data);
      } catch {
        /* ignora falhas pontuais de rede */
      }
    };
    const id = setInterval(poll, 1000);
    return () => clearInterval(id);
  }, [view, activeTicketId, accessToken, base]);

  const fetchTicket = async (ticketId, token) => {
    const { data } = await openApi.get(`${base}/tickets/${ticketId}`, {
      headers: { "X-Ticket-Access-Token": token },
    });
    setDetail(data);
    setActiveTicketId(ticketId);
    setAccessToken(token);
    try {
      sessionStorage.setItem(storageKey(companyId, ticketId), token);
    } catch {
      /* ignore */
    }
  };

  const goHome = () => {
    setView("home");
    setDetail(null);
    setActiveTicketId(null);
    setAccessToken(null);
    setProtocolInput("");
    setAccessPhoneInput("");
    setLinkedPortalTickets([]);
  };

  const openChatWithTicket = async (ticketId, token) => {
    setLoading(true);
    try {
      await fetchTicket(ticketId, token);
      setView("chat");
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || "Não foi possível abrir.";
      showToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const onAccessTicket = async () => {
    const raw = protocolInput.trim() || "";
    if (!raw) {
      showToast("Informe o código do ticket (protocolo).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await openApi.post(`${base}/tickets/access`, {
        protocol: raw,
        phone: accessPhoneInput.trim(),
      });
      const tickets = Array.isArray(data?.tickets) ? data.tickets : [];
      if (tickets.length === 0) {
        showToast("Não foi possível acessar.");
        return;
      }
      setLinkedPortalTickets(tickets);
      const first = tickets[0];
      await openChatWithTicket(first.id, first.accessToken);
    } catch (err) {
      const code = err?.response?.data?.error;
      const map = {
        ERR_PUBLIC_PANEL_TICKET_NOT_FOUND: "Protocolo não encontrado.",
        ERR_INVALID_PROTOCOL: "Formato de protocolo inválido. Ex.: #2026-0001",
        ERR_PASSWORD_REQUIRED:
          "Informe o código de acesso (telefone cadastrado no chamado).",
        ERR_INVALID_PASSWORD: "Código de acesso incorreto para este ticket.",
      };
      showToast(map[code] || "Não foi possível acessar.");
    } finally {
      setLoading(false);
    }
  };

  const selectLinkedPortalTicket = async (ticketId) => {
    const row = linkedPortalTickets.find((x) => x.id === ticketId);
    if (!row || ticketId === activeTicketId) return;
    setLoading(true);
    try {
      await fetchTicket(row.id, row.accessToken);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || "Não foi possível abrir.";
      showToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setView("create");
  };

  const submitCreate = async () => {
    const phone = portalPhone.trim();
    if (!phone) {
      showToast("Informe o WhatsApp.");
      return;
    }
    if (option2Stages.length > 0 && !portalCategoryStageId.trim()) {
      showToast(`Selecione: ${option2SelectLabel}`);
      return;
    }
    for (const cf of activeCustomFields) {
      const v = String(customFieldValues[cf.id] ?? "").trim();
      if (!v) {
        showToast(`Preencha: ${String(cf.question).trim()}`);
        return;
      }
    }
    setLoading(true);
    try {
      const openingCustomFields = {};
      activeCustomFields.forEach((cf) => {
        openingCustomFields[cf.id] = customFieldValues[cf.id];
      });
      const { data } = await openApi.post(`${base}/tickets/create`, {
        contactName: "",
        clientEmail: "",
        phone,
        category: portalCategoryStageId.trim(),
        subject: "",
        description: "",
        attachmentNames: [],
        openingCustomFields,
      });
      showToast("Ticket criado com sucesso!");
      setPortalPhone("");
      setPortalCategoryStageId("");
      setLinkedPortalTickets([
        {
          id: data.ticket.id,
          protocol: data.ticket.protocol || data.protocol || "",
          accessToken: data.accessToken,
        },
      ]);
      await openChatWithTicket(data.ticket.id, data.accessToken);
    } catch (err) {
      const code = err?.response?.data?.error;
      showToast(
        code === "ERR_INVALID_PORTAL_OPTION2"
          ? "Selecione uma opção válida."
          : "Erro ao criar ticket."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadPortalAttachment = useCallback(
    async (messageId, filename) => {
      if (activeTicketId == null || !accessToken) return;
      try {
        const res = await openApi.get(
          `${base}/tickets/${activeTicketId}/messages/${messageId}/attachment`,
          {
            responseType: "blob",
            headers: { "X-Ticket-Access-Token": accessToken },
          }
        );
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "anexo";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        showToast("Não foi possível baixar o anexo.");
      }
    },
    [activeTicketId, accessToken, base, showToast]
  );

  const openPortalAttachmentPreview = useCallback(
    async (messageId, filename) => {
      if (activeTicketId == null || !accessToken) return;
      setAttachmentPreview((s) => ({
        ...s,
        open: true,
        loading: true,
        messageId,
        url: "",
        filename: filename || "anexo",
        kind: "file",
      }));
      try {
        const res = await openApi.get(
          `${base}/tickets/${activeTicketId}/messages/${messageId}/attachment`,
          {
            responseType: "blob",
            headers: { "X-Ticket-Access-Token": accessToken },
          }
        );
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        if (attachmentPreviewUrlRef.current) {
          try {
            URL.revokeObjectURL(attachmentPreviewUrlRef.current);
          } catch {
            /* ignore */
          }
        }
        attachmentPreviewUrlRef.current = url;
        const kind = guessAttachmentKind(res?.headers?.["content-type"], filename);
        setAttachmentPreview((s) => ({
          ...s,
          open: true,
          loading: false,
          messageId,
          url,
          filename: filename || "anexo",
          kind,
        }));
      } catch {
        setAttachmentPreview((s) => ({ ...s, loading: false }));
        showToast("Não foi possível abrir a pré-visualização.");
      }
    },
    [activeTicketId, accessToken, base, showToast]
  );

  useEffect(() => {
    return () => {
      if (attachmentPreviewUrlRef.current) {
        try {
          URL.revokeObjectURL(attachmentPreviewUrlRef.current);
        } catch {
          /* ignore */
        }
        attachmentPreviewUrlRef.current = "";
      }
    };
  }, []);

  const sendMsg = async () => {
    if (String(detail?.situation || "").trim() === "archived") {
      showToast("Este ticket está arquivado e não permite novas interações.");
      return;
    }
    const text = msgDraft.trim();
    const files = pendingChatFiles;
    if (!text && files.length === 0) return;
    if (activeTicketId == null || !accessToken) return;
    setLoading(true);
    try {
      const url = `${base}/tickets/${activeTicketId}/messages`;
      const authHdr = { "X-Ticket-Access-Token": accessToken };

      if (files.length === 0) {
        await openApi.post(
          url,
          { body: text, attachmentName: null },
          { headers: authHdr }
        );
      } else {
        for (let i = 0; i < files.length; i += 1) {
          const formData = new FormData();
          formData.append("body", i === 0 ? text : "");
          formData.append("file", files[i]);
          await openApi.post(url, formData, {
            headers: { ...authHdr },
          });
        }
      }

      const { data } = await openApi.get(`${base}/tickets/${activeTicketId}`, {
        headers: authHdr,
      });
      setDetail(data);
      setMsgDraft("");
      setPendingChatFiles([]);
      if (fileChatRef.current) fileChatRef.current.value = "";
      showToast("Mensagem enviada com sucesso!");
    } catch {
      showToast("Erro ao enviar mensagem.");
    } finally {
      setLoading(false);
    }
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const stageLabel = (colKey, stageId) => {
    if (!panelConfig?.stages?.[colKey] || !stageId) return "—";
    const st = panelConfig.stages[colKey].find((s) => s.id === stageId);
    return st?.name || "—";
  };

  const optionRow = (n, colKey) => {
    const optTitle =
      (panelConfig?.optionNames?.[colKey] || "").trim() || `Opção ${n}`;
    const val = detail?.[`classificationOption${n}`] || "";
    const sub = stageLabel(colKey, val);
    const statusClass = val ? "opt-active" : "opt-inactive";
    const statusText = val ? "Ativo" : "Aguardando";
    return (
      <div key={colKey} className="option-item">
        <div className={`opt-num opt-${n}`}>{n}</div>
        <div className="opt-info">
          <div className="opt-title">{optTitle}</div>
          <div className="opt-sub">{sub}</div>
        </div>
        <span className={`opt-status ${statusClass}`}>{statusText}</span>
      </div>
    );
  };

  if (!companyId) {
    return (
      <div id="public-ticket-portal-root">
        <div className="public-portal-slug-missing">
          <p>Endereço do portal inválido. Use o link com o identificador da sua empresa.</p>
          <a href="/portal">Como acessar</a>
        </div>
      </div>
    );
  }

  const renderMessage = (msg) => {
    const isIn = msg.direction === "in";
    const isSys =
      !isIn && String(msg.body || "").includes("criado com sucesso");
    if (isSys) {
      return (
        <div key={msg.id} className="msg-row">
          <div className="msg-avatar avatar-sys">🤖</div>
          <div className="msg-bubble-wrap">
            <div className="msg-meta">
              <span className="msg-author">Sistema</span>
              <span className="msg-role role-sys">Automático</span>
              <span>{formatMsgTime(msg.createdAt)}</span>
            </div>
            <div className="msg-bubble sys-msg">{msg.body}</div>
          </div>
        </div>
      );
    }
    if (isIn) {
      return (
        <div key={msg.id} className="msg-row client">
          <div className="msg-avatar avatar-cli">
            {initials(detail?.contactName)}
          </div>
          <div className="msg-bubble-wrap">
            <div className="msg-meta">
              <span>{formatMsgTime(msg.createdAt)}</span>
              <span className="msg-role role-cli">Cliente</span>
              <span className="msg-author">{detail?.contactName || "Cliente"}</span>
            </div>
            <div className="msg-bubble">
              {msg.body ? <div className="msg-text">{msg.body}</div> : null}
              {msg.attachmentStorageKey && msg.attachmentName ? (
                <button
                  type="button"
                  className="msg-attach-link"
                  onClick={() =>
                    openPortalAttachmentPreview(msg.id, msg.attachmentName)
                  }
                >
                  📎 {msg.attachmentName}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={msg.id} className="msg-row atd">
        <div className="msg-avatar avatar-atd">AT</div>
        <div className="msg-bubble-wrap">
          <div className="msg-meta">
            <span className="msg-author">Atendente</span>
            <span className="msg-role role-atd">Atendente</span>
            <span>{formatMsgTime(msg.createdAt)}</span>
          </div>
          <div className="msg-bubble">
            {msg.body ? <div className="msg-text">{msg.body}</div> : null}
            {msg.attachmentStorageKey && msg.attachmentName ? (
              <button
                type="button"
                className="msg-attach-link"
                onClick={() =>
                  openPortalAttachmentPreview(msg.id, msg.attachmentName)
                }
              >
                📎 {msg.attachmentName}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const protocolTag = detail?.protocol || "";
  const isArchived = String(detail?.situation || "").trim() === "archived";

  return (
    <div id="public-ticket-portal-root">
      <div
        id="homePage"
        style={{ display: view === "home" ? "flex" : "none" }}
      >
        <div className="home-left">
          <div className="brand">
            <div className="brand-icon">🎫</div>
            <span className="brand-name">{BRAND}</span>
          </div>
          <h1>
            Acompanhe seus
            <br />
            chamados em
            <br />
            tempo real
          </h1>
          <p>
            Portal exclusivo para clientes acompanharem suas solicitações com
            total transparência.
          </p>
          <div className="features">
            <div className="feat">
              <div className="feat-ico">📊</div>
              Visualize o status atualizado dos seus tickets
            </div>
            <div className="feat">
              <div className="feat-ico">💬</div>
              Envie mensagens e interaja com o suporte
            </div>
            <div className="feat">
              <div className="feat-ico">📎</div>
              Anexe arquivos e documentos facilmente
            </div>
            <div className="feat">
              <div className="feat-ico">🔔</div>
              Acompanhe cada etapa do atendimento
            </div>
          </div>
        </div>
        <div className="home-right">
          <div className="home-card">
            <h2>Acesso ao Portal</h2>
            <p className="sub">Consulte ou abra um chamado rapidamente</p>
            <div className="access-box">
              <strong>1 — Acompanhar um chamado</strong>
              <p>
                Informe o <strong>código do ticket</strong> (protocolo) e o{" "}
                <strong>código de acesso</strong>: um dos números de{" "}
                <em>Contatos de celular</em> cadastrados nesse chamado pelo suporte.
              </p>
              <div className="input-wrap">
                <span className="ico">🎫</span>
                <input
                  type="text"
                  value={protocolInput}
                  onChange={(e) => setProtocolInput(e.target.value)}
                  placeholder="Código do ticket — ex: #2026-0001"
                  autoComplete="off"
                />
              </div>
              <div className="input-wrap pportal-access-phone-wrap">
                <span className="ico">📱</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={accessPhoneInput}
                  onChange={(e) => setAccessPhoneInput(e.target.value)}
                  placeholder="Telefone cadastrado no chamado (contato)"
                />
              </div>
              <p className="pportal-access-hint">
                Se o chamado não tiver telefone nem senha de acesso, deixe o campo do
                telefone em branco.
              </p>
              <button
                type="button"
                className="btn-primary"
                disabled={loading}
                onClick={onAccessTicket}
              >
                🔓 Acessar conversa
              </button>
            </div>
            <div className="divider">ou</div>
            <div className="access-box">
              <strong>2 — Criar um Ticket</strong>
              <p>Descreva sua solicitação e nossa equipe retornará em breve</p>
              <button
                type="button"
                className="btn-outline"
                onClick={openCreate}
              >
                ✏️ Criar Novo Ticket
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        id="chatPage"
        className={`page${view === "chat" ? " active" : ""}`}
      >
        <div className="topbar">
          <div className="topbar-brand">
            <div className="topbar-brand-ico">🎫</div>
            {BRAND}
          </div>
          <span className="ticket-tag">{protocolTag}</span>
          <button
            type="button"
            className="btn-back"
            style={{ marginLeft: "auto" }}
            onClick={goHome}
          >
            ← Voltar
          </button>
        </div>
        <div
          className={`chat-layout${
            linkedPortalTickets.length > 1 ? " chat-layout--with-linked" : ""
          }`}
        >
          {linkedPortalTickets.length > 1 ? (
            <aside className="pportal-linked-nav" aria-label="Seus chamados">
              <div className="pportal-linked-nav-title">Meus chamados</div>
              <p className="pportal-linked-nav-sub">
                Mesmo telefone em mais de um ticket — escolha qual deseja ver.
              </p>
              <ul className="pportal-linked-nav-list">
                {linkedPortalTickets.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`pportal-linked-nav-item${
                        activeTicketId === t.id ? " is-active" : ""
                      }`}
                      onClick={() => selectLinkedPortalTicket(t.id)}
                      disabled={loading}
                    >
                      <span className="pportal-linked-nav-protocol">
                        {t.protocol || `#${t.id}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}
          <div className="chat-main">
            <div className="chat-header">
              <div className="chat-header-ico">💬</div>
              <div className="chat-header-info">
                <div className="chat-title">
                  {detail?.subject || detail?.contactName || "—"}
                </div>
                <div className="chat-sub">
                  {option2SelectLabel}:{" "}
                  {stageLabel("2", detail?.classificationOption2) ||
                    detail?.classificationOption1 ||
                    "—"}
                </div>
              </div>
              <span
                className={`status-badge${
                  isArchived ? " status-badge--archived" : ""
                }`}
              >
                {isArchived ? "Arquivado" : "Em andamento"}
              </span>
            </div>
            <div className="messages-area" id="messagesArea">
              {(detail?.messages || []).map((m) => renderMessage(m))}
              <div ref={messagesEndRef} />
            </div>
            <div className="input-area">
              <div
                className={`attach-preview-bar${
                  pendingChatFiles.length ? " show" : ""
                }`}
              >
                {pendingChatFiles.map((f, i) => (
                  <div key={f.name + i} className="attach-tag">
                    📎 {f.name}
                    <button
                      type="button"
                      className="remove-tag"
                      onClick={() =>
                        setPendingChatFiles((prev) =>
                          prev.filter((_, j) => j !== i)
                        )
                      }
                      disabled={isArchived}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="input-row">
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => fileChatRef.current?.click()}
                  disabled={isArchived}
                  title={isArchived ? "Ticket arquivado" : "Anexar"}
                >
                  📎
                </button>
                <textarea
                  id="msgInput"
                  placeholder="Digite sua mensagem..."
                  rows={1}
                  value={msgDraft}
                  onChange={(e) => {
                    setMsgDraft(e.target.value);
                    autoResize(e.target);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMsg();
                    }
                  }}
                  disabled={isArchived}
                />
                <button
                  type="button"
                  className="send-btn"
                  onClick={sendMsg}
                  disabled={isArchived}
                  title={isArchived ? "Ticket arquivado" : "Enviar"}
                >
                  ➤
                </button>
              </div>
              <input
                ref={fileChatRef}
                type="file"
                id="fileInput"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setPendingChatFiles((prev) => [...prev, ...files]);
                }}
                disabled={isArchived}
              />
              {isArchived ? (
                <div className="pportal-archived-hint">
                  Este ticket está arquivado e não permite novas interações.
                </div>
              ) : null}
            </div>
          </div>
          <div className="sidebar">
            <div className="side-card">
              <h4>⚙️ Opções do Atendimento</h4>
              {optionRow(1, "1")}
              {optionRow(2, "2")}
              {optionRow(3, "3")}
            </div>
          </div>
        </div>
      </div>

      <div
        id="createPage"
        className={`page${view === "create" ? " active" : ""}`}
      >
        <div className="topbar">
          <div className="topbar-brand">
            <div className="topbar-brand-ico">🎫</div>
            {BRAND}
          </div>
          <button
            type="button"
            className="btn-back"
            style={{ marginLeft: "auto" }}
            onClick={goHome}
          >
            ← Voltar
          </button>
        </div>
        <div className="form-wrap">
          <div className="form-card">
            <h3>Abrir Novo Ticket</h3>
            <p>Informe seu WhatsApp e preencha os campos solicitados.</p>
            <div className="form-row">
              <div className="form-group">
                <label>WhatsApp *</label>
                <input
                  type="text"
                  placeholder="(00) 00000-0000"
                  value={portalPhone}
                  onChange={(e) => setPortalPhone(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>
                  {option2SelectLabel}
                  {option2Stages.length > 0 ? " *" : ""}
                </label>
                <select
                  value={portalCategoryStageId}
                  onChange={(e) => setPortalCategoryStageId(e.target.value)}
                  disabled={option2Stages.length === 0}
                >
                  <option value="">
                    {option2Stages.length === 0
                      ? "Nenhum estágio configurado na Opção 2"
                      : "Selecione..."}
                  </option>
                  {option2Stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {activeCustomFields.map((field) => {
              const q = String(field.question || "").trim();
              const val = customFieldValues[field.id] ?? "";
              const setVal = (v) =>
                setCustomFieldValues((s) => ({ ...s, [field.id]: v }));
              return (
                <div key={field.id} className="form-group">
                  <label>{q} *</label>
                  {field.type === "long" ? (
                    <textarea
                      rows={4}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                    />
                  ) : field.type === "number" ? (
                    <input
                      type="number"
                      step="any"
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                    />
                  ) : field.type === "date" ? (
                    <input
                      type="date"
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className="btn-primary"
              disabled={loading}
              onClick={submitCreate}
            >
              ✅ Enviar Ticket
            </button>
          </div>
        </div>
      </div>

      <div
        className={`pportal-toast${toast.show ? "" : " hidden"}`}
        style={{ display: toast.show ? "flex" : "none" }}
      >
        {toast.text}
      </div>

      {attachmentPreview.open ? (
        <div
          className="pportal-attach-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Pré-visualização do anexo"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAttachmentPreview();
          }}
        >
          <div className="pportal-attach-modal">
            <div className="pportal-attach-modal-header">
              <div className="pportal-attach-modal-title">Pré-visualização do anexo</div>
              <div className="pportal-attach-modal-actions">
                <button
                  type="button"
                  className="pportal-attach-modal-btn"
                  onClick={() => {
                    if (!attachmentPreview.messageId) {
                      showToast("Não foi possível localizar o anexo para download.");
                      return;
                    }
                    downloadPortalAttachment(
                      attachmentPreview.messageId,
                      attachmentPreview.filename
                    );
                  }}
                  disabled={attachmentPreview.loading}
                >
                  Baixar
                </button>
                <button
                  type="button"
                  className="pportal-attach-modal-btn pportal-attach-modal-btn--close"
                  onClick={closeAttachmentPreview}
                  aria-label="Fechar"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="pportal-attach-modal-body">
              {attachmentPreview.loading ? (
                <div className="pportal-attach-modal-loading">Carregando…</div>
              ) : !attachmentPreview.url ? (
                <div className="pportal-attach-modal-loading">
                  Não foi possível abrir a pré-visualização.
                </div>
              ) : attachmentPreview.kind === "image" ? (
                <img
                  className="pportal-attach-preview-media"
                  src={attachmentPreview.url}
                  alt={attachmentPreview.filename || "anexo"}
                />
              ) : attachmentPreview.kind === "video" ? (
                <video
                  className="pportal-attach-preview-media"
                  src={attachmentPreview.url}
                  controls
                />
              ) : attachmentPreview.kind === "pdf" ? (
                <iframe
                  className="pportal-attach-preview-frame"
                  title={attachmentPreview.filename || "PDF"}
                  src={attachmentPreview.url}
                />
              ) : (
                <div className="pportal-attach-modal-fallback">
                  <div className="pportal-attach-modal-filename">
                    {attachmentPreview.filename || "anexo"}
                  </div>
                  <div className="pportal-attach-modal-note">
                    Este tipo de arquivo não tem pré-visualização aqui.
                  </div>
                  <button
                    type="button"
                    className="pportal-attach-modal-btn"
                    onClick={() => {
                      if (!attachmentPreview.messageId) {
                        showToast("Não foi possível localizar o anexo para download.");
                        return;
                      }
                      downloadPortalAttachment(
                        attachmentPreview.messageId,
                        attachmentPreview.filename
                      );
                    }}
                  >
                    Baixar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PublicTicketPortalPage;
